import React, { useState } from 'react';
import { Clock, Mail, X } from 'lucide-react';

import { MISSIONS_AT, type MissionAT, type Niveau } from '../../data/missionsAssistanceTechnique';

interface Props {
  /** Adresse de l'équipe OmnIA, pour exprimer un besoin. */
  contact: string;
}

const ORDRE_VAGUES = ['Vague 1', 'Vague 2', 'Vague 3', 'Existant'];

// Les missions présentées : celles où une mission de conseil récurrente est
// documentée (activités TCA de Gavi, documents du PEV) et revient chaque
// année ou à chaque cycle. Les autres fiches de la note restent dans les
// données : A2 est épisodique, A8 et A9 ne sont pas établies comme missions
// récurrentes, A10 à A12 ne remplacent pas une expertise externe. A6 revient :
// en Guinée, l'UNICEF recrute à chaque campagne des consultants « chaîne du
// froid et gestion des vaccins ».
const MISSIONS_AFFICHEES = ['A1', 'A3', 'A4', 'A5', 'A6', 'A7'];

const NIVEAUX: Record<Niveau, string> = {
  Remplace: 'bg-eg-accent/[0.09] text-eg-accent',
  Augmente: 'bg-eg-sauge/15 text-eg-valide',
  Humain: 'bg-black/[0.05] text-eg-brun',
};

const Liste: React.FC<{ titre: string; elements: string[] }> = ({ titre, elements }) =>
  elements.length ? (
    <section className="mt-5">
      <h4 className="text-[12.5px] font-medium text-eg-accent">{titre}</h4>
      <ul className="mt-1.5 space-y-1 text-[13px] leading-relaxed text-eg-brun">
        {elements.map((e) => (
          <li key={e} className="flex gap-2">
            <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-eg-sauge" />
            <span>{e}</span>
          </li>
        ))}
      </ul>
    </section>
  ) : null;

/**
 * Les missions d'assistance technique que le PEV reçoit, et l'agent prévu pour
 * chacune, d'après la note de cadrage d'Efficience Globale.
 *
 * Rien ne se lance d'ici : aucune de ces missions n'est encore couverte comme
 * sa fiche l'exige. La page présente la feuille de route et recueille les
 * besoins.
 */
const MissionsAssistanceTechnique: React.FC<Props> = ({ contact }) => {
  const [ouverte, setOuverte] = useState<MissionAT | null>(null);

  // Rangées par vague, dans l'ordre où elles seront réalisées. Seules
  // paraissent les missions de conseil documentées et récurrentes.
  const missions = ORDRE_VAGUES.flatMap((vague) =>
    MISSIONS_AT.filter((m) => m.vague === vague && MISSIONS_AFFICHEES.includes(m.code)));

  const besoin = (mission: MissionAT) =>
    `mailto:${contact}?subject=${encodeURIComponent(`OmnIA, mission ${mission.code} ${mission.titre}`)}`;

  return (
    <div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {missions.map((mission) => (
          <button
            key={mission.code}
            type="button"
            onClick={() => setOuverte(mission)}
            className="flex flex-col rounded-2xl bg-eg-blanc p-4 text-left shadow-carte outline outline-1
              -outline-offset-1 outline-black/[0.07] transition-transform duration-150 hover:-translate-y-0.5"
          >
            <div className="flex items-center gap-2">
              <span className="rounded-md bg-eg-accent px-1.5 py-0.5 font-mono text-[10.5px] font-medium text-white">
                {mission.code}
              </span>
              <span className="text-[11px] text-eg-estompe">{mission.vague === 'Existant' ? 'Base existante' : mission.vague}</span>
              <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-black/[0.045] px-2 py-[3px]
                text-[11px] font-medium text-eg-gris">
                <Clock className="h-3 w-3" />
                En cours de réalisation
              </span>
            </div>
            <span className="mt-3 text-[15px] font-medium leading-snug text-eg-encre">{mission.titre}</span>
            <span className="mt-1.5 line-clamp-3 text-[12.5px] leading-[18px] text-eg-gris">{mission.change}</span>
          </button>
        ))}
      </div>

      {ouverte && (
        <div className="fixed inset-0 z-50 flex justify-end bg-eg-encre/25" onClick={() => setOuverte(null)}>
          <aside onClick={(e) => e.stopPropagation()} className="flex h-full w-full max-w-[720px] flex-col bg-eg-fond shadow-2xl">
            <div className="flex items-start gap-3 border-b border-eg-bordure px-6 py-5">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="rounded-md bg-eg-accent px-1.5 py-0.5 font-mono text-[10.5px] font-medium text-white">
                    {ouverte.code}
                  </span>
                  <span className="rounded-full bg-black/[0.045] px-2 py-[3px] text-[11px] font-medium text-eg-gris">
                    {ouverte.vague}
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-black/[0.045] px-2 py-[3px] text-[11px] font-medium text-eg-gris">
                    <Clock className="h-3 w-3" /> En cours de réalisation
                  </span>
                </div>
                <h2 className="mt-2.5 text-[19px] font-semibold tracking-tight text-eg-encre">{ouverte.titre}</h2>
              </div>
              <button
                type="button"
                onClick={() => setOuverte(null)}
                className="rounded-full p-1.5 text-eg-estompe hover:bg-black/[0.05] hover:text-eg-encre"
                aria-label="Fermer"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
              <dl className="grid gap-3 rounded-2xl bg-eg-papier px-4 py-3.5 text-[12.5px] sm:grid-cols-2">
                <div>
                  <dt className="text-eg-estompe">Mission de référence</dt>
                  <dd className="mt-0.5 text-eg-encre">{ouverte.mission}</dd>
                </div>
                <div>
                  <dt className="text-eg-estompe">Profil habituellement mobilisé</dt>
                  <dd className="mt-0.5 text-eg-encre">{ouverte.profil}</dd>
                </div>
              </dl>

              <section className="mt-5">
                <h4 className="text-[12.5px] font-medium text-eg-accent">Ce que l'agent change</h4>
                <p className="mt-1 text-[13.5px] leading-relaxed text-eg-brun">{ouverte.change}</p>
              </section>

              {ouverte.fonctionnement && (
                <section className="mt-5">
                  <h4 className="text-[12.5px] font-medium text-eg-accent">Fonctionnement habituel</h4>
                  <p className="mt-1 text-[13px] leading-relaxed text-eg-gris">{ouverte.fonctionnement}</p>
                </section>
              )}

              <section className="mt-5">
                <h4 className="text-[12.5px] font-medium text-eg-accent">Tâche par tâche</h4>
                <div className="mt-2 overflow-hidden rounded-2xl bg-eg-blanc shadow-carte outline outline-1 -outline-offset-1 outline-black/[0.07]">
                  {ouverte.taches.map((tache, index) => (
                    <div key={tache.tache} className={`px-4 py-3 ${index > 0 ? 'border-t border-eg-bordure' : ''}`}>
                      <div className="flex items-start gap-2">
                        <span className="min-w-0 flex-1 text-[13px] font-medium text-eg-encre">{tache.tache}</span>
                        <span className={`shrink-0 rounded-full px-2 py-[2px] text-[11px] font-medium ${NIVEAUX[tache.niveau] || NIVEAUX.Humain}`}>
                          {tache.niveau}
                        </span>
                      </div>
                      <div className="mt-1 text-[12px] text-eg-estompe">Aujourd'hui : {tache.habituel}</div>
                      <div className="mt-0.5 text-[12.5px] text-eg-brun">{tache.avec_agent}</div>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-[11.5px] text-eg-estompe">
                  Remplace : l'agent produit, une personne nommée valide. Augmente : la personne reste
                  productrice, l'agent prépare et vérifie. Humain : hors du champ de l'IA.
                </p>
              </section>

              {ouverte.utilisateurs && (
                <section className="mt-5">
                  <h4 className="text-[12.5px] font-medium text-eg-accent">Utilisateurs</h4>
                  <p className="mt-1 text-[13px] text-eg-brun">{ouverte.utilisateurs}</p>
                </section>
              )}
              <Liste titre="Sources et données d'entrée" elements={ouverte.sources} />
              <Liste titre="Livrables" elements={ouverte.livrables} />
              <Liste titre="Garde-fous et validation humaine" elements={ouverte.garde_fous} />
              <Liste titre="Prérequis à confirmer" elements={ouverte.prerequis} />
            </div>

            <div className="flex items-center gap-3 border-t border-eg-bordure px-6 py-4">
              <p className="min-w-0 flex-1 text-[12px] text-eg-estompe">
                Cet agent est en cours de réalisation. Un besoin précis aide à le prioriser.
              </p>
              {contact && (
                <a
                  href={besoin(ouverte)}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-eg-ombre px-3.5 py-1.5
                    text-[13px] font-medium text-white hover:bg-eg-profond"
                >
                  <Mail className="h-3.5 w-3.5" />
                  Exprimer un besoin
                </a>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default MissionsAssistanceTechnique;
